import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, fmtEUR, formatApiError } from "../lib/api";
import {
  Trash2, Save, FileDown, Loader2, Plus, ArrowLeft,
  Layers, DoorOpen, Truck, Package, Construction, UserSquare2, StickyNote, FileCheck2, ReceiptText, Ruler, PencilRuler, Mail, BellRing, PenLine,
} from "lucide-react";
import { EmailDialog, EmailHistory } from "../components/EmailDialog";

const FALLBACK_COUNTRIES = [
  { name: "Deutschland", mwstPercent: 19.0 },
  { name: "Österreich", mwstPercent: 20.0 },
  { name: "Schweiz", mwstPercent: 8.1 },
];

const PRODUCT_SUBTITLES = {
  ASTRAL: "Der schlichte Alu-Einzelstabzaun",
  ECLIPSE: "Der kreative Alu-Lamellenzaun",
  GALACTIC: "Der minimalistische Alu-Lamellenzaun",
  LUNAR: "Der waagerechte Alu-Lamellenzaun",
  ORBIT: "Der waagerechte Alu-Lamellenzaun",
  SOLARIS: "Der zeitlose Alu-Lattenzaun",
  STELLAR: "Der waagerechte Alu-Lamellenzaun",
  "STELLAR 160": "Der waagerechte Alu-Lamellenzaun",
  INFINITY: "Der grenzenlose Alu-Zaun",
  RAHMEN: "Belattung bauseits",
  DOPPELSTABMATTE_656: "Doppelstabmatte 6/5/6",
};

const PRODUCT_IMAGES = {
  ASTRAL: "/brand/models/astral.png",
  ECLIPSE: "/brand/models/eclipse.png",
  GALACTIC: "/brand/models/galactic.png",
  LUNAR: "/brand/models/lunar.png",
  ORBIT: "/brand/models/orbit.png",
  SOLARIS: "/brand/models/solaris.png",
  STELLAR: "/brand/models/stellar.png",
  "STELLAR 160": "/brand/models/stellar.png",
  INFINITY: "/brand/models/infinity.png",
  DOPPELSTABMATTE_656: "/brand/models/dsm.png",
  RAHMEN: "/brand/models/rahmen.png",
};

const emptyQuote = () => ({
  customer: { name: "", company: "", address: "", zipCode: "", city: "", country: "Deutschland", email: "", phone: "" },
  project: { name: "", location: "", notes: "" },
  items: [],
  totalDiscountPercent: 0,
  extras: { lieferung: 0, montage: 0, montagematerial: 0, andereKosten: 0, andereKostenLabel: "" },
  mwstPercent: null, // null = auto from country
  validUntil: "",
  status: "entwurf",
  notes: "",
  notesTemplateId: "",
  planner: null,
});

const STATUS_OPTIONS = [
  { value: "entwurf", label: "Entwurf" },
  { value: "gesendet", label: "Gesendet" },
  { value: "angenommen", label: "Angenommen" },
  { value: "abgelehnt", label: "Abgelehnt" },
];

const GATE_TYPES = [
  { key: "gehtuere", label: "Gehtüre", icon: DoorOpen },
  { key: "doppelflugel", label: "Doppelflügeltor", icon: DoorOpen },
  { key: "schiebetor", label: "Freitragendes Schiebetor", icon: Truck },
];

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricelist, setPricelist] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({ countries: FALLBACK_COUNTRIES, surcharges: {} });
  const [error, setError] = useState("");

  const [quote, setQuote] = useState(emptyQuote());
  const [mwstOverridden, setMwstOverridden] = useState(false);
  const [quoteMeta, setQuoteMeta] = useState({ quoteNumber: null, id: null });
  const [confirmation, setConfirmation] = useState(null);
  const [confLoading, setConfLoading] = useState(false);
  const [anzahlung, setAnzahlung] = useState(null);
  const [anzLoading, setAnzLoading] = useState(false);
  const [anzOpen, setAnzOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [inquiryRef, setInquiryRef] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const sendReminder = async () => {
    if (!window.confirm("Erinnerung mit Angebots-PDF an dieselben Empfänger senden?")) return;
    setReminderBusy(true); setError("");
    try {
      const r = await api.post(`/quotes/${quoteMeta.id}/reminder`);
      setEmails((p) => [...p, r.data]);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setReminderBusy(false); }
  };
  const [emailOpen, setEmailOpen] = useState(false);
  const [distance, setDistance] = useState(null);
  const [distLoading, setDistLoading] = useState(false);
  const [distError, setDistError] = useState("");

  const calcDistance = async () => {
    setDistLoading(true); setDistError(""); setDistance(null);
    try {
      const r = await api.post("/distance", {
        zipCode: quote.customer.zipCode || "",
        city: quote.customer.city || "",
        country: quote.customer.country || "Deutschland",
      });
      setDistance(r.data);
    } catch (e) {
      setDistError(formatApiError(e.response?.data?.detail) || "Fehler bei der Berechnung");
    } finally {
      setDistLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [pl, q, cust, tpls, st] = await Promise.all([
          api.get("/pricelist"),
          isNew ? Promise.resolve(null) : api.get(`/quotes/${id}`),
          api.get("/customers").catch(() => ({ data: [] })),
          api.get("/note-templates").catch(() => ({ data: [] })),
          api.get("/settings").catch(() => ({ data: null })),
        ]);
        setPricelist(pl.data);
        setCustomers(cust.data || []);
        setTemplates(tpls.data || []);
        if (st.data) setSettings(st.data);
        if (q?.data) {
          setQuote({
            customer: q.data.customer,
            project: q.data.project,
            items: q.data.items,
            totalDiscountPercent: q.data.totalDiscountPercent || 0,
            extras: q.data.extras || { lieferung: 0, montage: 0, montagematerial: 0, andereKosten: 0, andereKostenLabel: "" },
            mwstPercent: q.data.mwstPercent ?? null,
            validUntil: q.data.validUntil || "",
            status: q.data.status,
            notes: q.data.notes || "",
            notesTemplateId: q.data.notesTemplateId || "",
            planner: q.data.planner || null,
          });
          setMwstOverridden(true); // existing quote has explicit value
          setQuoteMeta({ quoteNumber: q.data.quoteNumber, id: q.data.id });
          setConfirmation(q.data.confirmation || null);
          setAnzahlung(q.data.anzahlung || null);
          setEmails(q.data.emails || []);
        } else if (isNew) {
          const d = new Date();
          d.setDate(d.getDate() + 30);
          // Load default template if exists
          const defTpl = (tpls.data || []).find((t) => t.isDefault);
          setQuote({
            ...emptyQuote(),
            validUntil: d.toISOString().slice(0, 10),
            notes: defTpl ? defTpl.body : "",
            notesTemplateId: defTpl ? defTpl.id : "",
          });
          setMwstOverridden(false);
          setQuoteMeta({ quoteNumber: null, id: null });
          setConfirmation(null); setEmails([]); setInquiryRef(null); setDistance(null); setDistError("");
          setAnzahlung(null);
        }
        const prefill = location.state?.prefillItems;
        if (prefill?.length) {
          setQuote((prev) => ({
            ...prev,
            items: [...prev.items.filter((i) => !i.config?.fromPlanner), ...prefill.map((i) => ({ ...i, id: crypto.randomUUID() }))],
            planner: location.state.planner || prev.planner,
            ...(location.state.inquiry ? {
              customer: { ...prev.customer, ...location.state.inquiry.customer },
              project: { ...prev.project, name: prev.project?.name || location.state.inquiry.projectName || "",
                         notes: [prev.project?.notes, location.state.inquiry.ral ? `Farbe: ${location.state.inquiry.ral}` : "", location.state.inquiry.message ? `Kundenanfrage: ${location.state.inquiry.message}` : ""].filter(Boolean).join("\n") },
            } : {}),
          }));
          if (location.state.inquiry) setInquiryRef(location.state.inquiry);
          window.history.replaceState({}, "");
        }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  // Calculate totals locally
  const mwstFor = (country) => settings.countries.find((c) => c.name === country)?.mwstPercent ?? 19;
  const totals = useMemo(() => {
    let subtotal = 0, discountable = 0;
    const items = quote.items.map((it) => {
      const base = (it.total || 0);
      const lineAfter = base * (1 - (it.discountPercent || 0) / 100);
      subtotal += lineAfter;
      if (!it.noDiscount) discountable += lineAfter;
      return { ...it, totalAfterDiscount: lineAfter };
    });
    subtotal = Math.round(subtotal * 100) / 100;
    const discAmt = Math.round((discountable * (quote.totalDiscountPercent || 0)) / 100 * 100) / 100;
    const netoAfterDiscount = Math.round((subtotal - discAmt) * 100) / 100;
    const lieferung = Number(quote.extras?.lieferung) || 0;
    const montage = Number(quote.extras?.montage) || 0;
    const montagematerial = Number(quote.extras?.montagematerial) || 0;
    const andere = Number(quote.extras?.andereKosten) || 0;
    const nettoTotal = Math.round((netoAfterDiscount + lieferung + montage + montagematerial + andere) * 100) / 100;
    // MwSt
    const autoMwst = mwstFor(quote.customer.country);
    const mwstPercent = quote.mwstPercent != null ? Number(quote.mwstPercent) : autoMwst;
    const mwstAmount = Math.round(nettoTotal * mwstPercent / 100 * 100) / 100;
    const bruttoTotal = Math.round((nettoTotal + mwstAmount) * 100) / 100;
    return {
      subtotal,
      totalDiscountPercent: quote.totalDiscountPercent || 0,
      totalDiscountAmount: discAmt,
      netoAfterDiscount,
      lieferung,
      montage,
      montagematerial,
      andereKosten: andere,
      andereKostenLabel: quote.extras?.andereKostenLabel || "Andere Kosten",
      total: nettoTotal,
      mwstPercent,
      mwstAmount,
      bruttoTotal,
      items,
    };
  }, [quote.items, quote.totalDiscountPercent, quote.extras, quote.mwstPercent, quote.customer.country, settings]);

  // Auto-update MwSt when country changes (unless user manually overrode it)
  useEffect(() => {
    if (mwstOverridden) return;
    const auto = mwstFor(quote.customer.country);
    setQuote((p) => ({ ...p, mwstPercent: auto }));
    // eslint-disable-next-line
  }, [quote.customer.country, mwstOverridden, settings]);

  const setCustomer = (k, v) => setQuote((p) => ({ ...p, customer: { ...p.customer, [k]: v } }));
  const setProject = (k, v) => setQuote((p) => ({ ...p, project: { ...p.project, [k]: v } }));

  const addItem = (item) => setQuote((p) => ({ ...p, items: [...p.items, { ...item, id: crypto.randomUUID() }] }));
  const updateItem = (idx, patch) => setQuote((p) => {
    const items = [...p.items];
    items[idx] = { ...items[idx], ...patch };
    return { ...p, items };
  });
  const removeItem = (idx) => setQuote((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        customer: quote.customer,
        project: quote.project,
        items: quote.items,
        totalDiscountPercent: Number(quote.totalDiscountPercent) || 0,
        extras: {
          lieferung: Number(quote.extras?.lieferung) || 0,
          montage: Number(quote.extras?.montage) || 0,
          montagematerial: Number(quote.extras?.montagematerial) || 0,
          andereKosten: Number(quote.extras?.andereKosten) || 0,
          andereKostenLabel: quote.extras?.andereKostenLabel || "",
        },
        mwstPercent: quote.mwstPercent != null ? Number(quote.mwstPercent) : null,
        validUntil: quote.validUntil || null,
        status: quote.status,
        notes: quote.notes,
        notesTemplateId: quote.notesTemplateId || null,
        planner: quote.planner || null,
      };
      let res;
      if (quoteMeta.id) {
        res = await api.put(`/quotes/${quoteMeta.id}`, payload);
      } else {
        res = await api.post("/quotes", payload);
      }
      setQuoteMeta({ quoteNumber: res.data.quoteNumber, id: res.data.id });
      if (inquiryRef?.id) {
        api.patch(`/inquiries/${inquiryRef.id}`, { status: "angebot", quoteId: res.data.id }).catch(() => {});
        setInquiryRef(null);
      }
      navigate(`/quote/${res.data.id}`, { replace: true });
      return res.data;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const loadCustomer = (cId) => {
    if (!cId) return;
    const c = customers.find((x) => x.id === cId);
    if (!c) return;
    setQuote((p) => ({
      ...p,
      customer: {
        name: c.name || "", company: c.company || "", address: c.address || "",
        zipCode: c.zipCode || "", city: c.city || "", country: c.country || "Deutschland",
        email: c.email || "", phone: c.phone || "",
      },
    }));
    setMwstOverridden(false); // re-apply country-based mwst
  };

  const saveCustomer = async () => {
    setError("");
    try {
      const payload = { ...quote.customer };
      const r = await api.post("/customers", payload);
      setCustomers((prev) => [...prev, r.data]);
      alert(`Kunde "${r.data.company || r.data.name}" wurde in der Kundendatenbank gespeichert.`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const applyTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (t) {
      setQuote((p) => ({ ...p, notes: t.body, notesTemplateId: t.id }));
    } else {
      setQuote((p) => ({ ...p, notesTemplateId: "" }));
    }
  };

  const downloadPdf = async () => {
    try {
      const saved = quoteMeta.id ? { id: quoteMeta.id, quoteNumber: quoteMeta.quoteNumber } : await save();
      const res = await api.get(`/quotes/${saved.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Angebot_${saved.quoteNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const createConfirmation = async () => {
    setConfLoading(true); setError("");
    try {
      const saved = quoteMeta.id ? { id: quoteMeta.id, quoteNumber: quoteMeta.quoteNumber } : await save();
      const c = await api.post(`/quotes/${saved.id}/confirmation`);
      setConfirmation(c.data);
      const res = await api.get(`/quotes/${saved.id}/confirmation/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.data.number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setConfLoading(false);
    }
  };

  const createAnzahlung = async (amount) => {
    setAnzLoading(true); setError("");
    try {
      const saved = await save();
      const a = await api.post(`/quotes/${saved.id}/anzahlung`, { amount: Number(amount) || 0 });
      setAnzahlung(a.data);
      setAnzOpen(false);
      const res = await api.get(`/quotes/${saved.id}/anzahlung/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const el = document.createElement("a");
      el.href = url; el.download = `${a.data.number}.pdf`; el.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAnzLoading(false);
    }
  };

  const openEmail = async () => {
    setError("");
    try {
      if (!quoteMeta.id) await save();
      setEmailOpen(true);
    } catch { /* error already set */ }
  };

  if (loading) return <div className="p-10 text-sm text-zinc-500">Wird geladen...</div>;

  return (
    <div className="flex flex-col lg:flex-row" data-testid="quote-builder-page">
      {/* Main scrollable area */}
      <div className="flex-1 p-4 md:p-10 max-w-4xl min-w-0">
        <button onClick={() => navigate(-1)} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4">
          <ArrowLeft size={13} /> Zurück
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 md:mb-8 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 font-mono">
              {quoteMeta.quoteNumber || "Entwurf"}
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900" data-testid="quote-title">
              {isNew ? "Neues Angebot" : `Angebot ${quoteMeta.quoteNumber}`}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end md:max-w-xl" data-testid="quote-action-bar">
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-quote-button"
              className="flex-1 md:flex-none border border-zinc-300 hover:bg-zinc-50 text-zinc-900 text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              Speichern
            </button>
            <button
              onClick={downloadPdf}
              data-testid="download-pdf-button"
              className="flex-1 md:flex-none bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2"
            >
              <FileDown size={14}/> PDF erstellen
            </button>
            <button
              onClick={openEmail}
              data-testid="send-email-button"
              title="Per E-Mail an den Kunden senden"
              className="flex-1 md:flex-none border border-zinc-300 hover:bg-zinc-50 text-zinc-900 text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2"
            >
              <Mail size={14}/> E-Mail
            </button>
            <button
              onClick={createConfirmation}
              disabled={confLoading}
              data-testid="create-confirmation-button"
              title="Auftragsbestätigung / Proforma-Rechnung als PDF"
              className={`flex-1 md:flex-none text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 ${
                confirmation
                  ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                  : "border border-zinc-300 hover:bg-zinc-50 text-zinc-900"
              }`}
            >
              {confLoading ? <Loader2 size={14} className="animate-spin"/> : <FileCheck2 size={14}/>}
              {confirmation ? `${confirmation.number} (PDF)` : "AB / Proforma erstellen"}
            </button>
            <button
              onClick={() => { setAnzOpen(true); }}
              disabled={anzLoading}
              data-testid="create-anzahlung-button"
              title="Anzahlungsrechnung (AR) erstellen – mit erhaltenem Anzahlungsbetrag"
              className={`flex-1 md:flex-none text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 ${
                anzahlung
                  ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                  : "border border-zinc-300 hover:bg-zinc-50 text-zinc-900"
              }`}
            >
              {anzLoading ? <Loader2 size={14} className="animate-spin"/> : <ReceiptText size={14}/>}
              {anzahlung ? `${anzahlung.number} (PDF)` : "AR / Anzahlung erstellen"}
            </button>
          </div>
        </div>

        {anzOpen && (
          <AnzahlungDialog
            current={anzahlung}
            brutto={quote.totals?.bruttoTotal ?? totals.bruttoTotal}
            loading={anzLoading}
            onClose={() => setAnzOpen(false)}
            onConfirm={createAnzahlung}
          />
        )}

        {error && (
          <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4">{error}</div>
        )}
        {emailOpen && quoteMeta.id && (
          <EmailDialog quoteId={quoteMeta.id} quoteNumber={quoteMeta.quoteNumber} customerEmail={quote.customer.email}
                       hasConfirmation={!!confirmation} confirmationNumber={confirmation?.number}
                       onClose={() => setEmailOpen(false)}
                       onSent={(rec) => { setEmails((p) => [...p, rec]); setEmailOpen(false); if (rec.docType === "angebot" && quote.status === "entwurf") setQuote((p) => ({ ...p, status: "gesendet" })); }} />
        )}
        {emails.length > 0 && (
          <Section title="E-Mail-Verlauf" testId="email-section" right={
            <div className="flex items-center gap-3">
              {emails.some((e) => e.docType === "angebot") && (
                <button onClick={sendReminder} disabled={reminderBusy} data-testid="send-reminder-button"
                        className="text-xs font-medium flex items-center gap-1 px-2.5 py-1 border border-zinc-900 text-zinc-900 rounded-sm hover:bg-zinc-900 hover:text-white disabled:opacity-50">
                  <BellRing size={12}/> {reminderBusy ? "Wird gesendet…" : "Erinnerung jetzt senden"}
                </button>
              )}
              <span className="text-xs text-zinc-500">{emails.length} E-Mail(s)</span>
            </div>
          }>
            <EmailHistory emails={emails} />
          </Section>
        )}

        {/* Kunde & Projekt */}
        <Section title="Kundendaten" testId="customer-section"
                 right={
                   <button onClick={saveCustomer} data-testid="save-customer-button"
                           disabled={!quote.customer.company && !quote.customer.name}
                           className="text-xs text-zinc-600 hover:text-zinc-900 underline disabled:opacity-40 disabled:no-underline">
                     + Als neuen Kunden speichern
                   </button>
                 }>
          {customers.length > 0 && (
            <div className="mb-3">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block flex items-center gap-1">
                <UserSquare2 size={11}/> Bestehenden Kunden laden
              </label>
              <select onChange={(e) => loadCustomer(e.target.value)} data-testid="customer-selector"
                      defaultValue=""
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black">
                <option value="">— Neuen Kunden eingeben oder auswählen —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.company, c.name].filter(Boolean).join(" — ")} {c.city ? `(${c.city})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Grid cols={2}>
            <Input label="Firma / Name, Nachname" value={quote.customer.company} onChange={(v) => setCustomer("company", v)} testId="customer-company" />
            <Input label="Ansprechpartner" value={quote.customer.name} onChange={(v) => setCustomer("name", v)} testId="customer-name" />
            <Input label="Adresse" value={quote.customer.address} onChange={(v) => setCustomer("address", v)} testId="customer-address" full />
            <Input label="PLZ" value={quote.customer.zipCode} onChange={(v) => setCustomer("zipCode", v)} testId="customer-zip" />
            <Input label="Stadt" value={quote.customer.city} onChange={(v) => setCustomer("city", v)} testId="customer-city" />
            <Select label="Land" value={quote.customer.country || "Deutschland"}
                   onChange={(v) => { setCustomer("country", v); setMwstOverridden(false); }} testId="customer-country"
                   options={settings.countries.map((c) => ({
                     value: c.name,
                     label: `${c.name} (${c.mwstPercent}% MwSt.)`,
                   }))} />
            <Input label="E-Mail" value={quote.customer.email} onChange={(v) => setCustomer("email", v)} testId="customer-email" />
            <Input label="Telefon" value={quote.customer.phone} onChange={(v) => setCustomer("phone", v)} testId="customer-phone" />
          </Grid>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={calcDistance}
              disabled={distLoading || !quote.customer.zipCode?.trim()}
              data-testid="calc-distance-button"
              title="Fahrstrecke von Tuzla (nur intern, erscheint nicht im Angebot)"
              className="text-xs border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50 flex items-center gap-1.5 text-zinc-700 disabled:opacity-50"
            >
              <Truck size={12}/> {distLoading ? "Wird berechnet..." : "Entfernung berechnen"}
            </button>
            {distance && (
              <span className="text-xs font-mono text-zinc-700 bg-zinc-100 border border-zinc-200 px-2 py-1 rounded-sm" data-testid="distance-result">
                ≈ {distance.distanceKm} km{distance.estimated ? " (Schätzung)" : ""} · ca. {distance.durationHours} h Fahrt
                <span className="text-zinc-400 font-sans ml-1.5">(nur intern — nicht im Angebot)</span>
              </span>
            )}
            {distError && <span className="text-xs text-red-600" data-testid="distance-error">{distError}</span>}
          </div>
        </Section>

        <Section title="Projektdaten" testId="project-section">
          <Grid cols={2}>
            <Input label="Projektname" value={quote.project.name} onChange={(v) => setProject("name", v)} testId="project-name" />
            <Input label="Standort" value={quote.project.location} onChange={(v) => setProject("location", v)} testId="project-location" />
            <Input label="Notiz" value={quote.project.notes} onChange={(v) => setProject("notes", v)} testId="project-notes" full multiline />
          </Grid>
          <Grid cols={3} className="mt-4">
            <Input label="Gültig bis" type="date" value={quote.validUntil} onChange={(v) => setQuote((p) => ({...p, validUntil: v}))} testId="quote-validity" />
            <Select label="Status" value={quote.status} onChange={(v) => setQuote((p) => ({...p, status: v}))} testId="quote-status"
                   options={STATUS_OPTIONS}/>
            <Input label="Gesamtrabatt %" type="number" value={quote.totalDiscountPercent}
                  onChange={(v) => setQuote((p) => ({...p, totalDiscountPercent: Number(v)}))} testId="total-discount" />
          </Grid>
          <Grid cols={2} className="mt-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">
                MwSt % {!mwstOverridden && <span className="text-zinc-400 normal-case tracking-normal">(auto aus Land)</span>}
              </label>
              <input type="number" step="0.1" value={quote.mwstPercent ?? ""}
                     onChange={(e) => { setMwstOverridden(true); setQuote((p) => ({...p, mwstPercent: e.target.value === "" ? null : Number(e.target.value)})); }}
                     data-testid="mwst-percent-input"
                     className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black"/>
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none" data-testid="mwst-zero-toggle">
                <input type="checkbox" checked={Number(quote.mwstPercent) === 0}
                       onChange={(e) => { setMwstOverridden(true); setQuote((p) => ({ ...p, mwstPercent: e.target.checked ? 0 : mwstFor(p.customer.country) })); }}
                       className="accent-zinc-900 w-4 h-4" data-testid="mwst-zero-checkbox" />
                <span className="text-xs text-zinc-700">Ohne MwSt. (0 % – steuerfrei)</span>
              </label>
            </div>
            {mwstOverridden && (
              <div className="flex items-end">
                <button onClick={() => { setMwstOverridden(false); }} data-testid="mwst-reset-button"
                        className="text-xs text-zinc-600 hover:text-zinc-900 underline">
                  Auf Standard aus Land zurücksetzen
                </button>
              </div>
            )}
          </Grid>
        </Section>

        {/* ADD ITEM PANELS */}
        <Section title="Position hinzufügen" testId="add-item-section">
          <ItemAdders pricelist={pricelist} surcharges={settings.surcharges} onAdd={addItem} />
        </Section>

        {/* ITEMS LIST */}
        <Section title="Positionen" testId="items-section" right={
          <div className="flex items-center gap-3">
            {(quote.planner?.sections?.some((s) => s.points?.length > 0 || s.ansicht?.items?.length > 0) || quote.planner?.points?.length > 0 || quote.planner?.ansicht?.items?.length > 0) && (
              <button onClick={() => navigate("/planner", { state: { plan: quote.planner, quoteId: quoteMeta.id } })} data-testid="edit-planner-button"
                      className="text-xs font-medium flex items-center gap-1 text-zinc-700 hover:text-zinc-900 underline">
                <PencilRuler size={12}/> Zaunplan bearbeiten
              </button>
            )}
            <span className="text-xs text-zinc-500">{quote.items.length} Position(en)</span>
          </div>
        }>
          {quote.items.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200">
              Noch keine Positionen. Wählen Sie oben ein Modell oder Zubehör.
            </div>
          ) : (
            <div className="border border-zinc-200">
              <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                <div className="col-span-1">Pos.</div>
                <div className="col-span-5">Beschreibung</div>
                <div className="col-span-1 text-center">Menge</div>
                <div className="col-span-2 text-right">Einzelpreis</div>
                <div className="col-span-1 text-center">Rabatt %</div>
                <div className="col-span-1 text-right">Gesamt</div>
                <div className="col-span-1"></div>
              </div>
              {quote.items.map((it, idx) => (
                <ItemRow key={it.id} item={it} index={idx} onChange={(p) => updateItem(idx, p)} onRemove={() => removeItem(idx)} />
              ))}
            </div>
          )}
        </Section>

        {/* EXTRAS */}
        <Section title="Zusätzliche Kosten" testId="extras-section">
          <p className="text-xs text-zinc-500 mb-3">
            Tragen Sie Beträge ein. Felder mit 0 werden im PDF nicht angezeigt.
          </p>
          <Grid cols={2}>
            <Input label="Lieferung (€)" type="number" value={quote.extras?.lieferung || 0}
                  onChange={(v) => setQuote((p) => ({...p, extras: {...p.extras, lieferung: Number(v) || 0}}))}
                  testId="extra-lieferung" />
            <Input label="Montage (€)" type="number" value={quote.extras?.montage || 0}
                  onChange={(v) => setQuote((p) => ({...p, extras: {...p.extras, montage: Number(v) || 0}}))}
                  testId="extra-montage" />
            <Input label="Montagematerial und Verpackung (€)" type="number" value={quote.extras?.montagematerial || 0}
                  onChange={(v) => setQuote((p) => ({...p, extras: {...p.extras, montagematerial: Number(v) || 0}}))}
                  testId="extra-montagematerial" />
            <Input label="Andere Kosten (€)" type="number" value={quote.extras?.andereKosten || 0}
                  onChange={(v) => setQuote((p) => ({...p, extras: {...p.extras, andereKosten: Number(v) || 0}}))}
                  testId="extra-andere" />
          </Grid>
          {(Number(quote.extras?.andereKosten) > 0) && (
            <div className="mt-3">
              <Input label={'Bezeichnung für „Andere Kosten" (optional)'} value={quote.extras?.andereKostenLabel || ""}
                    onChange={(v) => setQuote((p) => ({...p, extras: {...p.extras, andereKostenLabel: v}}))}
                    testId="extra-andere-label" full />
            </div>
          )}
        </Section>

        {/* HINWEISE */}
        <Section title="Hinweise (PDF-Fußtext)" testId="hinweise-section"
                 right={<span className="text-xs text-zinc-500"><StickyNote size={11} className="inline mr-1"/>Wird im PDF unten angezeigt</span>}>
          {templates.length > 0 && (
            <div className="mb-3">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">
                Vorlage wählen
              </label>
              <select value={quote.notesTemplateId || ""} onChange={(e) => applyTemplate(e.target.value)} data-testid="hinweise-template-select"
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black">
                <option value="">— Keine Vorlage / freier Text —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.title}{t.isDefault ? " (Standard)" : ""}</option>
                ))}
              </select>
            </div>
          )}
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">
            Hinweistext
          </label>
          <textarea value={quote.notes || ""} onChange={(e) => setQuote((p) => ({ ...p, notes: e.target.value }))}
                    data-testid="hinweise-text"
                    rows={6}
                    placeholder="Hinweise zum Angebot — mehrzeilig, Zeilen mit '-' werden als Aufzählung formatiert."
                    className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black"/>
          {templates.length === 0 && (
            <p className="text-xs text-zinc-500 mt-2">
              Tipp: Admins können unter „Administration → Hinweise" wiederverwendbare Vorlagen anlegen.
            </p>
          )}
        </Section>
      </div>

      {/* Sticky pricing summary */}
      <aside className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-zinc-200 bg-zinc-50/60 p-6 lg:sticky lg:top-0 self-start lg:h-screen overflow-auto" data-testid="totals-panel">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Zusammenfassung</div>
        <div className="space-y-3 text-sm">
          <Row label="Zwischensumme" value={fmtEUR(totals.subtotal)} mono />
          {totals.totalDiscountPercent > 0 && (
            <Row label={`Gesamtrabatt (${totals.totalDiscountPercent}%)`} value={`− ${fmtEUR(totals.totalDiscountAmount)}`} mono red />
          )}
          {totals.lieferung > 0 && <Row label="Lieferung" value={fmtEUR(totals.lieferung)} mono />}
          {totals.montage > 0 && <Row label="Montage" value={fmtEUR(totals.montage)} mono />}
          {totals.montagematerial > 0 && <Row label="Montagematerial u. Verpackung" value={fmtEUR(totals.montagematerial)} mono />}
          {totals.andereKosten > 0 && (
            <Row label={totals.andereKostenLabel || "Andere Kosten"} value={fmtEUR(totals.andereKosten)} mono />
          )}
          <div className="border-t border-zinc-300 pt-3 mt-3 space-y-2">
            <Row label="Netto" value={fmtEUR(totals.total)} mono bold />
            <Row label={`zzgl. MwSt. (${(totals.mwstPercent ?? 0).toFixed(1)}%)`} value={fmtEUR(totals.mwstAmount)} mono />
          </div>
          <div className="border-t-2 border-zinc-900 pt-3 mt-2">
            <Row label="Brutto gesamt" value={fmtEUR(totals.bruttoTotal)} mono large bold />
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-6">
          MwSt-Satz automatisch nach Lieferland
        </div>
      </aside>
    </div>
  );
}

// ============================= SUBKOMPONENTE =============================

function Section({ title, children, right, testId }) {
  return (
    <section className="mb-10" data-testid={testId}>
      <div className="flex items-end justify-between mb-3 pb-2 border-b border-zinc-200">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function Grid({ children, cols = 2, className = "" }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3 ${className}`}>{children}</div>;
}

function Input({ label, value, onChange, full, type = "text", testId, multiline }) {
  const cls = "w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black";
  const [draft, setDraft] = useState(null);
  const isNum = type === "number";
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testId} className={cls + " min-h-[60px]"} />
      ) : isNum ? (
        <input type="number" inputMode="decimal" value={draft !== null ? draft : (value ?? "")} data-testid={testId} className={cls}
               onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); }} onBlur={() => setDraft(null)} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={cls} />
      )}
    </div>
  );
}

function NumCell({ value, onChange, min, max, testId, className }) {
  const [draft, setDraft] = useState(null);
  return <input type="number" inputMode="numeric" min={min} max={max} data-testid={testId} className={className}
    value={draft !== null ? draft : (value ?? "")}
    onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); }} onBlur={() => setDraft(null)} />;
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
              className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Row({ label, value, mono, large, red, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-zinc-700 ${bold ? "font-semibold text-zinc-900" : ""} ${large ? "text-sm" : "text-xs"}`}>{label}</span>
      <span className={`${mono ? "font-mono tabular-nums" : ""} ${red ? "text-red-600" : "text-zinc-900"} ${bold ? "font-semibold" : ""} ${large ? "text-lg" : "text-sm"}`}>{value}</span>
    </div>
  );
}

function ItemRow({ item, index, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-2 md:gap-3 px-3 md:px-4 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-white items-start text-sm" data-testid={`item-row-${index}`}>
      <div className="col-span-1 text-xs font-mono text-zinc-400 pt-2">{index + 1}.</div>
      <div className="col-span-11 md:col-span-5">
        <div className="font-medium text-zinc-900 text-sm">{item.description}</div>
        {item.details && <div className="text-xs text-zinc-500 mt-0.5">{item.details}</div>}
        {item.type === "custom" && (
          <label className="inline-flex items-center gap-1.5 mt-1 text-[11px] text-zinc-600 cursor-pointer">
            <input type="checkbox" className="accent-black" checked={!!item.noDiscount} onChange={(e) => onChange({ noDiscount: e.target.checked })} data-testid={`item-${index}-nodiscount`} /> nicht rabattfähig
          </label>
        )}
        {item.type !== "custom" && item.noDiscount && <div className="text-[11px] text-zinc-600 mt-1" data-testid={`item-${index}-nodiscount-badge`}>nicht rabattfähig</div>}
        <div className="flex items-center gap-3 mt-2 md:hidden">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Einzel: <span className="font-mono text-zinc-700">{fmtEUR(item.unitPrice)}</span></span>
        </div>
      </div>
      <div className="col-span-3 md:col-span-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 md:hidden">Menge</div>
        <NumCell min="1" value={item.quantity} testId={`item-${index}-qty`}
              className="w-full px-2 py-1 border border-zinc-200 text-center text-sm rounded-sm"
              onChange={(v) => onChange({ quantity: Number(v) || 1, total: (item.unitPrice || 0) * (Number(v) || 1) })} />
      </div>
      <div className="hidden md:block col-span-2 text-right font-mono text-xs text-zinc-700 pt-1.5">{fmtEUR(item.unitPrice)}</div>
      <div className="col-span-3 md:col-span-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 md:hidden">Rabatt %</div>
        <NumCell min="0" max="100" value={item.discountPercent || 0} testId={`item-${index}-discount`}
              className="w-full px-2 py-1 border border-zinc-200 text-center text-sm rounded-sm"
              onChange={(v) => onChange({ discountPercent: Number(v) })} />
      </div>
      <div className="col-span-4 md:col-span-1 text-right font-mono text-sm font-medium text-zinc-900 pt-0 md:pt-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 md:hidden">Total</div>
        {fmtEUR(((item.total || 0) * (1 - (item.discountPercent || 0) / 100)))}
      </div>
      <div className="col-span-2 md:col-span-1 text-right">
        <button onClick={onRemove} className="text-zinc-400 hover:text-red-600 mt-1" data-testid={`item-${index}-remove`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ============================= ITEM ADDERS =============================

function ItemAdders({ pricelist, surcharges, onAdd }) {
  const [tab, setTab] = useState("zaunelement");
  const tabs = [
    { key: "zaunelement", label: "Zaunelement", icon: Layers },
    { key: "gates", label: "Tore", icon: DoorOpen },
    { key: "pfosten", label: "Pfosten", icon: Construction },
    { key: "accessories", label: "Zubehör", icon: Package },
    { key: "custom", label: "Freie Position", icon: PenLine },
  ];
  return (
    <div className="border border-zinc-200">
      <div className="flex border-b border-zinc-200 bg-zinc-50 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`adder-tab-${t.key}`}
                  className={`flex items-center gap-2 px-4 md:px-5 py-3 text-xs font-medium uppercase tracking-widest border-r border-zinc-200 transition-colors whitespace-nowrap ${
                    tab === t.key ? "bg-white text-zinc-900 border-b-2 border-b-black -mb-px" : "text-zinc-600 hover:bg-white"
                  }`}>
            <t.icon size={13}/> {t.label}
          </button>
        ))}
      </div>
      <div className="p-4 md:p-5">
        {tab === "zaunelement" && <ZaunelementAdder pricelist={pricelist} surcharges={surcharges} onAdd={onAdd} />}
        {tab === "gates" && <GatesAdder pricelist={pricelist} surcharges={surcharges} onAdd={onAdd} />}
        {tab === "pfosten" && <PfostenAdder pricelist={pricelist} surcharges={surcharges} onAdd={onAdd} />}
        {tab === "accessories" && <AccessoriesAdder pricelist={pricelist} onAdd={onAdd} />}
        {tab === "custom" && <CustomItemAdder onAdd={onAdd} />}
      </div>
    </div>
  );
}

function CustomItemAdder({ onAdd }) {
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [noDiscount, setNoDiscount] = useState(false);
  const price = parseFloat(String(unitPrice).replace(",", ".")) || 0;
  const total = Math.round(price * (Number(quantity) || 0) * 100) / 100;
  const canAdd = name.trim().length >= 2 && price > 0 && Number(quantity) >= 1;
  const inCls = "w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black";
  const add = () => {
    onAdd({ type: "custom", productKey: null, description: name.trim(), details: details.trim() || null, config: { custom: true },
      quantity: Number(quantity), unitPrice: price, total, noDiscount });
    setName(""); setDetails(""); setUnitPrice(""); setQuantity(1); setNoDiscount(false);
  };
  return (
    <div data-testid="custom-adder">
      <p className="text-xs text-zinc-600 mb-3">Artikel, der nicht in der Preisliste steht (Sonderanfertigung, Fremdartikel, einmalige Leistung). Preis netto pro Stück.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="lg:col-span-2">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Artikel / Bezeichnung *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Sonder-Eckwinkel 90°" className={inCls} data-testid="custom-name" />
        </div>
        <NumberInput label="Einzelpreis (€ netto)" value={unitPrice} onChange={setUnitPrice} testId="custom-price" step={0.5} min={0} />
        <NumberInput label="Menge" value={quantity} onChange={setQuantity} testId="custom-qty" min={1} step={1} />
        <div className="lg:col-span-4">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Beschreibung (optional)</label>
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={2} placeholder="Details, Maße, Farbe …" className={inCls} data-testid="custom-details" />
        </div>
      </div>
      <div className="mb-4 border border-zinc-200 bg-zinc-50/50 px-3 py-2">
        <CheckOption label="Nicht rabattfähig" price="vom Gesamtrabatt ausgenommen" checked={noDiscount} onChange={setNoDiscount} testId="custom-nodiscount" />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-zinc-50 border border-zinc-200">
        <div className="text-xs text-zinc-600">Freie Positionen erscheinen im Angebot wie alle anderen Positionen; „nicht rabattfähig“ nimmt die Position vom Gesamtrabatt aus.</div>
        <div className="flex items-center justify-between sm:justify-end gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Position</div>
            <div className="font-mono text-lg text-zinc-900" data-testid="custom-preview-total">{fmtEUR(total)}</div>
          </div>
          <button disabled={!canAdd} onClick={add} data-testid="custom-add-button"
                  className="bg-black text-white text-sm font-medium px-5 py-2.5 rounded-sm hover:bg-zinc-800 disabled:opacity-40 flex items-center gap-2">
            <Plus size={14}/> Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckOption({ label, price, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-2 text-sm text-zinc-700 cursor-pointer select-none py-1">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
             data-testid={testId} className="w-4 h-4 mt-0.5 accent-black shrink-0" />
      <span>
        {label}
        {price && <span className="text-xs text-zinc-500 font-mono ml-1.5">{price}</span>}
      </span>
    </label>
  );
}

function ZaunelementAdder({ pricelist, surcharges, onAdd }) {
  const products = Object.keys(pricelist.products).filter((k) => pricelist.products[k].zaunelement?.length);
  const [productKey, setProductKey] = useState(products[0]);
  const [height, setHeight] = useState(1500);
  const [width, setWidth] = useState(2000);
  const [quantity, setQuantity] = useState(1);
  const [schraeg, setSchraeg] = useState(false);
  const [variant, setVariant] = useState("");
  const [option, setOption] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const product = pricelist.products[productKey] || {};
  const variants = product.zaunelementVariants || [];
  const options = product.options || [];

  const selectProduct = (k) => {
    setProductKey(k);
    const p = pricelist.products[k] || {};
    setVariant(p.zaunelementVariants?.length ? p.zaunelementVariants[0].name : "");
    setOption("");
  };

  const onCalc = async () => {
    setLoading(true);
    try {
      const r = await api.post("/calc/zaunelement", {
        productKey, height: +height, width: +width, quantity: +quantity, schraeg,
        variant: variant || null, option: option || null,
      });
      setPreview(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { onCalc(); /* eslint-disable-next-line */ }, [productKey, height, width, quantity, schraeg, variant, option]);

  const detailParts = [`B ${width} × H ${height} mm, ${quantity} Stk.`];
  const extras = [];
  if (variant && variants.length) extras.push(variant);
  if (option) extras.push(option);
  if (schraeg) extras.push("Schräges Zaunfeld/Geländer");

  return (
    <div>
      <ModelPicker products={products} value={productKey} onChange={selectProduct} testIdPrefix="z-model" />
      <ZaunfeldHelper pricelist={pricelist} onApply={async (w, n, post) => {
        setWidth(w); setQuantity(n);
        const ph = +height + 60;
        try {
          const r = await api.post("/calc/pfosten", { profil: post.profil, height: ph, quantity: post.count, winkelschnitt: false, seitenmontageplatte: false });
          onAdd({ type: "accessory", productKey: "pfosten", description: `Pfosten ${post.profil}`, details: `Höhe ${ph} mm, ${post.count} Stk.`,
            config: { profil: post.profil, height: ph, winkelschnitt: false, seitenmontageplatte: false }, quantity: post.count, unitPrice: r.data.unitPrice || 0, total: r.data.total || 0 });
        } catch (e) { /* kein Pfostenpreis – nur Breite/Menge übernommen */ }
      }} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 mb-4">
        <NumberInput label="Breite (mm)" value={width} onChange={setWidth} testId="z-width" step={100} />
        <NumberInput label="Höhe (mm)" value={height} onChange={setHeight} testId="z-height" step={50} />
        <NumberInput label="Menge" value={quantity} onChange={setQuantity} testId="z-qty" min={1} step={1} />
      </div>
      {+width > 0 && +width < 1000 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 mb-4" data-testid="z-min-width-hint">
          Mindestabrechnung: Breiten unter 1000 mm werden mit 1000 mm berechnet.
        </div>
      )}
      {variants.length > 0 && (
        <div className="mb-4">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Ausführung</label>
          <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid="z-variant-select"
                  className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
            {variants.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
          </select>
        </div>
      )}
      {(options.length > 0 || surcharges) && (
        <div className="mb-4 border border-zinc-200 bg-zinc-50/50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Optionen</div>
          {options.length > 0 && (
            <div className="mb-2">
              <select value={option} onChange={(e) => setOption(e.target.value)} data-testid="z-option-select"
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
                <option value="">— Keine Zusatzoption —</option>
                {options.map((o) => (
                  <option key={o.name} value={o.name}>
                    {o.name}{o.type === "per_lfm_flat" ? ` (+${fmtEUR(o.pricePerMeter)}/lfm)` : " (Preis/lfm nach Höhe)"}
                  </option>
                ))}
              </select>
              {option && preview?.optionAmount > 0 && (
                <div className="text-xs text-zinc-600 mt-1" data-testid="z-option-amount">
                  Zusatzoption: +{fmtEUR(preview.optionAmount)}/Stk.
                </div>
              )}
            </div>
          )}
          <CheckOption
            label="Schräges Zaunfeld oder Geländer"
            price={`+${fmtEUR(surcharges?.schraegesZaunfeld ?? 28)}/lfm`}
            checked={schraeg} onChange={setSchraeg} testId="z-schraeg-checkbox" />
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-zinc-50 border border-zinc-200">
        <div className="text-xs text-zinc-600">
          {loading ? "Wird berechnet..." : (schraeg && preview?.schraegAmount ? `inkl. Schrägzuschnitt: +${fmtEUR(preview.schraegAmount)}/Stk.` : "")}
        </div>
        <div className="flex items-center justify-between md:justify-end gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Position</div>
            <div className="font-mono text-lg text-zinc-900" data-testid="z-preview-total">{fmtEUR(preview?.total)}</div>
          </div>
          <button onClick={() => onAdd({
            type: "zaunelement", productKey, description: `${productKey} Zaunelement`,
            details: `${detailParts.join(", ")}${extras.length ? " — inkl. " + extras.join(", ") : ""}`,
            config: { height, width, schraeg, variant: variant || null, option: option || null },
            quantity: +quantity, unitPrice: preview?.unitPrice || 0, total: preview?.total || 0,
          })} data-testid="z-add-button"
            className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-zinc-800 flex items-center gap-2">
            <Plus size={14}/> Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function ZaunfeldHelper({ pricelist, onApply }) {
  const [open, setOpen] = useState(false);
  const profiles = (pricelist.accessories?.pfosten?.profilTypes || []).map((p) => p.profil);
  const [length, setLength] = useState(10000);
  const [profil, setProfil] = useState(profiles.find((p) => p.startsWith("100x100mm")) || profiles[0] || "");
  const [dimIdx, setDimIdx] = useState(0);
  const [maxField, setMaxField] = useState(2000);

  const dims = (profil.match(/\d+/g) || []).slice(0, 2).map(Number);
  const postWidth = dims[dimIdx] ?? dims[0] ?? 0;
  const L = +length, M = +maxField;
  let result = null;
  if (L > 0 && M > 0 && postWidth > 0 && L > postWidth * 2) {
    let n = 1;
    while (n < 10000 && (L - (n + 1) * postWidth) / n >= M) n++;
    const fw = (L - (n + 1) * postWidth) / n;
    if (fw > 0) result = { fields: n, posts: n + 1, fieldWidth: Math.floor(fw) };
  }

  return (
    <div className="mb-4 border border-dashed border-zinc-300 bg-zinc-50/60" data-testid="zaunfeld-helper">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="zaunfeld-helper-toggle"
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-600 hover:text-zinc-900">
        <span className="flex items-center gap-2"><Ruler size={13}/> Hilfe: Zaunfeldlänge berechnen</span>
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <NumberInput label="Gesamtlänge Zaun (mm)" value={length} onChange={setLength} testId="zh-length" step={100} />
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Pfosten</label>
              <select value={profil} onChange={(e) => { setProfil(e.target.value); setDimIdx(0); }} data-testid="zh-profil-select"
                      className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
                {profiles.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Pfostenmaß in Zaunrichtung</label>
              <div className="flex gap-1">
                {dims.map((d, i) => (
                  <button key={i} type="button" onClick={() => setDimIdx(i)} data-testid={`zh-dim-${d}`}
                          className={`flex-1 px-2 py-2 text-sm font-mono border rounded-sm ${dimIdx === i ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500"}`}>
                    {d} mm
                  </button>
                ))}
              </div>
            </div>
            <NumberInput label="Max. Feldbreite (mm)" value={maxField} onChange={setMaxField} testId="zh-max-field" step={100} />
          </div>
          {result ? (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white border border-zinc-200 px-3 py-2">
              <div className="text-sm text-zinc-800" data-testid="zh-result">
                <span className="font-semibold">{result.posts} Pfosten</span> ({profil}, {postWidth} mm) und{" "}
                <span className="font-semibold">{result.fields} Felder à {result.fieldWidth} mm</span>
                <span className="text-xs text-zinc-500 ml-2">(Feld &lt; {M} mm)</span>
              </div>
              <button type="button" onClick={() => onApply(result.fieldWidth, result.fields, { profil, count: result.posts })} data-testid="zh-apply-button"
                      className="text-xs font-medium px-3 py-1.5 border border-zinc-900 text-zinc-900 rounded-sm hover:bg-zinc-900 hover:text-white whitespace-nowrap">
                Übernehmen (Breite + Menge + {result.posts} Pfosten)
              </button>
            </div>
          ) : (
            <div className="text-xs text-zinc-500" data-testid="zh-result">Bitte gültige Länge, Pfosten und Feldbreite eingeben.</div>
          )}
        </div>
      )}
    </div>
  );
}

function GatesAdder({ pricelist, surcharges, onAdd }) {
  const products = Object.keys(pricelist.products).filter((k) => pricelist.products[k].gehtuere || pricelist.products[k].doppelflugeltore || pricelist.products[k].freitragendeSchiebetore || pricelist.products[k].gatePriceSource);
  const [productKey, setProductKey] = useState(products[0]);
  const [gateType, setGateType] = useState("gehtuere");
  const [height, setHeight] = useState(1500);
  const [width, setWidth] = useState(1350);
  const [quantity, setQuantity] = useState(1);
  const [tueranschlag, setTueranschlag] = useState(false);
  const [zylinder, setZylinder] = useState(false);
  const [doppelstabmatte, setDoppelstabmatte] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const isRahmen = productKey === "RAHMEN";
  const hasGateOptions = gateType === "gehtuere" || gateType === "doppelflugel";
  const anschlagPrice = gateType === "gehtuere" ? (surcharges?.tueranschlagGehtuere ?? 60) : (surcharges?.tueranschlagDoppelfluegel ?? 90);

  const onCalc = async () => {
    setLoading(true);
    try {
      const r = await api.post("/calc/gate", {
        productKey, gateType, height: +height, width: +width, quantity: +quantity,
        tueranschlag: hasGateOptions && tueranschlag,
        zylinder: hasGateOptions && zylinder,
        doppelstabmatte: isRahmen && doppelstabmatte,
      });
      setPreview(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { onCalc(); /* eslint-disable-next-line */ }, [productKey, gateType, height, width, quantity, tueranschlag, zylinder, doppelstabmatte]);

  const optionDetails = [];
  if (hasGateOptions && tueranschlag) optionDetails.push("Durchgehender Türanschlag / Spaltabdeckung");
  if (hasGateOptions && zylinder) optionDetails.push("Zylinder");
  if (isRahmen && doppelstabmatte && preview?.doppelstabmatteAvailable) optionDetails.push("Füllung Doppelstabmatte 6/5/6");

  return (
    <div>
      <ModelPicker products={products} value={productKey} onChange={setProductKey} testIdPrefix="g-model" />
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Tor-Typ</label>
          <select value={gateType} onChange={(e) => setGateType(e.target.value)} data-testid="g-type-select"
                  className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
            {GATE_TYPES.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </div>
        <NumberInput label="Breite (mm)" value={width} onChange={setWidth} testId="g-width" step={50} />
        <NumberInput label="Höhe (mm)" value={height} onChange={setHeight} testId="g-height" step={50} />
        <NumberInput label="Menge" value={quantity} onChange={setQuantity} testId="g-qty" min={1} />
      </div>
      {(hasGateOptions || isRahmen) && (
        <div className="mb-4 border border-zinc-200 bg-zinc-50/50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Optionen</div>
          {hasGateOptions && (
            <>
              <CheckOption
                label="Durchgehender Türanschlag / Spaltabdeckung"
                price={`+${fmtEUR(anschlagPrice)}/Stk.`}
                checked={tueranschlag} onChange={setTueranschlag} testId="g-tueranschlag-checkbox" />
              <CheckOption
                label="Zylinder"
                price={`+${fmtEUR(surcharges?.zylinder ?? 37)}/Stk.`}
                checked={zylinder} onChange={setZylinder} testId="g-zylinder-checkbox" />
            </>
          )}
          {isRahmen && (
            <>
              <CheckOption
                label="Füllung Doppelstabmatte 6/5/6"
                price="Preis lt. Preisliste (€/lfm nach Höhe)"
                checked={doppelstabmatte} onChange={setDoppelstabmatte} testId="g-doppelstabmatte-checkbox" />
              {doppelstabmatte && preview?.doppelstabmatteAvailable === false && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 mt-1" data-testid="g-dsm-warning">
                  Doppelstabmatte-Preise sind in der Preisliste noch nicht hinterlegt. Bitte Preisliste (Excel) aktualisieren.
                </div>
              )}
              {doppelstabmatte && preview?.doppelstabmatteAvailable && (
                <div className="text-xs text-zinc-600 mt-1" data-testid="g-dsm-price">
                  Füllung: +{fmtEUR(preview.doppelstabmatteAmount)}/Stk.
                </div>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-zinc-50 border border-zinc-200">
        <div className="text-xs text-zinc-600">
          {loading ? "Wird berechnet..." : (preview?.note || "")}
          {!loading && preview?.maxiSystemAmount > 0 && (
            <div className="text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 mt-1" data-testid="g-maxi-info">
              + {preview.maxiSystemName}: {fmtEUR(preview.maxiSystemAmount)}/Stk. (wird als eigene Position hinzugefügt)
            </div>
          )}
        </div>
        <div className="flex items-center justify-between md:justify-end gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Position</div>
            <div className="font-mono text-lg text-zinc-900" data-testid="g-preview-total">{fmtEUR(preview?.total)}</div>
          </div>
          <button onClick={() => {
            onAdd({
              type: gateType, productKey,
              description: `${productKey} ${GATE_TYPES.find(g => g.key === gateType).label}`,
              details: `B ${width} × H ${height} mm, ${quantity} Stk.${optionDetails.length ? " — inkl. " + optionDetails.join(", ") : ""}`,
              config: { height, width, gateType, tueranschlag: hasGateOptions && tueranschlag, zylinder: hasGateOptions && zylinder, doppelstabmatte: isRahmen && doppelstabmatte },
              quantity: +quantity, unitPrice: preview?.unitPrice || 0, total: preview?.total || 0,
            });
            if (preview?.maxiSystemAmount > 0) {
              onAdd({
                type: "custom", productKey,
                description: preview.maxiSystemName,
                details: `für ${productKey} Freitragendes Schiebetor B ${width} mm, ${quantity} Stk.`,
                config: { maxiSystem: true, gateWidth: width },
                quantity: +quantity, unitPrice: preview.maxiSystemAmount, total: preview.maxiSystemTotal,
              });
            }
          }} data-testid="g-add-button"
            className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-zinc-800 flex items-center gap-2">
            <Plus size={14}/> Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function PfostenAdder({ pricelist, surcharges, onAdd }) {
  const pf = pricelist.accessories.pfosten;
  const profiles = (pf?.profilTypes || []).map((p) => p.profil);
  const [profil, setProfil] = useState(profiles[0] || "");
  const [height, setHeight] = useState(1500);
  const [quantity, setQuantity] = useState(1);
  const [winkelschnitt, setWinkelschnitt] = useState(false);
  const [seitenmontageplatte, setSeitenmontageplatte] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const available = !!(pf && pf.type === "pfosten_matrix" && profiles.length);

  useEffect(() => {
    if (!available || !profil) return;
    (async () => {
      setLoading(true); setError("");
      try {
        const r = await api.post("/calc/pfosten", { profil, height: +height, quantity: +quantity, winkelschnitt, seitenmontageplatte });
        setPreview(r.data);
      } catch (e) {
        setPreview(null);
        setError(formatApiError(e.response?.data?.detail) || "");
      } finally { setLoading(false); }
    })();
  }, [profil, height, quantity, winkelschnitt, seitenmontageplatte, available]);

  if (!available) {
    return <div className="text-sm text-zinc-500 py-4">Pfosten-Preisliste nicht verfügbar.</div>;
  }

  const optionDetails = [];
  if (winkelschnitt) optionDetails.push("Winkelschnitt");
  if (seitenmontageplatte) optionDetails.push("Seitenmontageplatte direkt Abstand 10 mm");

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Profiltyp</label>
          <select value={profil} onChange={(e) => setProfil(e.target.value)} data-testid="p-profil-select"
                  className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
            {pf.profilTypes.map((p) => <option key={p.profil} value={p.profil}>{p.profil}</option>)}
          </select>
        </div>
        <NumberInput label="Höhe (mm)" value={height} onChange={setHeight} testId="p-height" step={100} />
        <NumberInput label="Menge" value={quantity} onChange={setQuantity} testId="p-qty" min={1} step={1} />
        <div className="flex items-end">
          <div className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Einzelpreis</div>
            <div className="font-mono text-base font-semibold text-zinc-900" data-testid="p-unit-price">
              {loading ? "..." : preview?.unitPrice ? fmtEUR(preview.unitPrice) : "—"}
            </div>
          </div>
        </div>
      </div>
      <div className="mb-4 border border-zinc-200 bg-zinc-50/50 px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Optionen</div>
        <CheckOption
          label="Winkelschnitt bei Pfosten"
          price={`+${fmtEUR(surcharges?.winkelschnitt ?? 44)}/Stk.`}
          checked={winkelschnitt} onChange={setWinkelschnitt} testId="p-winkelschnitt-checkbox" />
        <CheckOption
          label="Seitenmontageplatte direkt Abstand 10 mm"
          price={`+${fmtEUR(surcharges?.seitenmontageplatte ?? 49)}/Stk.`}
          checked={seitenmontageplatte} onChange={setSeitenmontageplatte} testId="p-seitenmontageplatte-checkbox" />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-zinc-50 border border-zinc-200">
        <div className="text-xs text-zinc-600">
          {error ? <span className="text-red-600">{error}</span> : loading ? "Wird berechnet..." : ""}
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Position</div>
            <div className="font-mono text-lg text-zinc-900" data-testid="p-preview-total">{fmtEUR(preview?.total)}</div>
          </div>
          <button
            disabled={!preview?.unitPrice}
            onClick={() => onAdd({
              type: "accessory", productKey: "pfosten",
              description: `Pfosten ${profil}`,
              details: `Höhe ${height} mm, ${quantity} Stk.${optionDetails.length ? " — inkl. " + optionDetails.join(", ") : ""}`,
              config: { profil, height, winkelschnitt, seitenmontageplatte },
              quantity: +quantity, unitPrice: preview?.unitPrice || 0, total: preview?.total || 0,
            })} data-testid="p-add-button"
            className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-zinc-800 flex items-center gap-2 disabled:opacity-50 whitespace-nowrap">
            <Plus size={14}/> Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function AccessoriesAdder({ pricelist, onAdd }) {
  // Filter out pfosten (has its own tab)
  const keys = Object.keys(pricelist.accessories).filter((k) => k !== "pfosten");
  const [accessoryKey, setAccessoryKey] = useState(keys[0]);
  const [itemIndex, setItemIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const acc = pricelist.accessories[accessoryKey];
  const item = acc?.items?.[itemIndex];
  const total = (item?.price || 0) * quantity;

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Kategorie</label>
          <select value={accessoryKey} onChange={(e) => { setAccessoryKey(e.target.value); setItemIndex(0); }} data-testid="a-category-select"
                  className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
            {keys.map((k) => <option key={k} value={k}>{pricelist.accessories[k].title}</option>)}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Artikel</label>
          <select value={itemIndex} onChange={(e) => setItemIndex(Number(e.target.value))} data-testid="a-item-select"
                  className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm">
            {(acc?.items || []).map((i, idx) => (
              <option key={idx} value={idx}>{i.description} — {fmtEUR(i.price)}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <NumberInput label="Menge" value={quantity} onChange={setQuantity} testId="a-qty" min={1} />
      </div>
      <div className="flex items-center justify-between p-4 bg-zinc-50 border border-zinc-200">
        <div className="text-xs text-zinc-600">Einzelpreis: <span className="font-mono">{fmtEUR(item?.price)}</span></div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Position</div>
            <div className="font-mono text-lg text-zinc-900" data-testid="a-preview-total">{fmtEUR(total)}</div>
          </div>
          <button onClick={() => onAdd({
            type: "accessory", productKey: accessoryKey,
            description: `${acc.title}`,
            details: item?.description,
            config: { accessoryKey, itemIndex },
            quantity: +quantity, unitPrice: item?.price || 0, total,
          })} data-testid="a-add-button"
            className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-zinc-800 flex items-center gap-2">
            <Plus size={14}/> Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, testId, min = 0, step = 1 }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
             min={min} step={step}
             className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black" />
    </div>
  );
}

function ModelPicker({ products, value, onChange, testIdPrefix = "model" }) {
  return (
    <div className="mb-4" data-testid={`${testIdPrefix}-picker`}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2 block">Modell wählen</label>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {products.map((p) => {
          const selected = p === value;
          const img = PRODUCT_IMAGES[p];
          return (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              data-testid={`${testIdPrefix}-card-${p.replace(/\s+/g, "-")}`}
              className={`group relative border text-left transition-all ${
                selected
                  ? "border-zinc-900 ring-2 ring-zinc-900 ring-offset-1"
                  : "border-zinc-200 hover:border-zinc-400"
              }`}
            >
              <div className="aspect-[7/8] bg-white overflow-hidden flex items-center justify-center">
                {img ? (
                  <img
                    src={img}
                    alt={p}
                    className="w-full h-full object-contain transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="text-zinc-300 text-xs uppercase tracking-widest">
                    Kein Bild
                  </div>
                )}
              </div>
              <div className="px-2 py-2 bg-white border-t border-zinc-100">
                <div className="font-semibold text-xs tracking-wide text-zinc-900">{p}</div>
                <div className="text-[10px] text-zinc-500 leading-tight mt-0.5 line-clamp-2">
                  {PRODUCT_SUBTITLES[p] || ""}
                </div>
              </div>
              {selected && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">
                  ✓
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function AnzahlungDialog({ current, brutto, loading, onClose, onConfirm }) {
  const [amount, setAmount] = useState(current?.amount != null ? String(current.amount) : "");
  const val = Number(amount) || 0;
  const rest = Math.max(0, Math.round(((brutto || 0) - val) * 100) / 100);
  const eur2 = (n) => (n == null ? "—" : new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="anzahlung-dialog" onMouseDown={onClose}>
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-5" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <ReceiptText size={18} className="text-emerald-700" />
          <h3 className="text-base font-semibold text-zinc-900">Anzahlungsrechnung (AR)</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Geben Sie den bereits erhaltenen Anzahlungsbetrag ein. Der offene Restbetrag wird automatisch berechnet.</p>
        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Erhaltene Anzahlung (€)</label>
        <input
          type="number" min="0" step="0.01" autoFocus value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="anzahlung-amount-input"
          className="w-full px-3 py-2.5 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black"
          placeholder="0,00"
        />
        <div className="flex items-center gap-2 mt-2" data-testid="anzahlung-quick">
          <span className="text-[10px] uppercase tracking-widest text-zinc-400">Schnellwahl</span>
          {[35, 50].map((pct) => (
            <button key={pct} type="button" onClick={() => setAmount(String(Math.round((brutto || 0) * pct) / 100))}
                    data-testid={`anzahlung-pct-${pct}`}
                    className="px-2.5 py-1 text-xs font-medium border border-zinc-300 rounded-sm text-zinc-700 hover:border-emerald-700 hover:text-emerald-700 transition-colors">
              {pct}% vom Brutto
            </button>
          ))}
        </div>
        <div className="mt-3 text-xs bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 space-y-1" data-testid="anzahlung-preview">
          <div className="flex justify-between"><span className="text-zinc-500">Gesamtbetrag brutto</span><span className="font-mono text-zinc-800">{eur2(brutto || 0)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Erhaltene Anzahlung</span><span className="font-mono text-zinc-800">− {eur2(val)}</span></div>
          <div className="flex justify-between border-t border-zinc-200 pt-1 mt-1"><span className="font-semibold text-zinc-900">Offener Restbetrag</span><span className="font-mono font-semibold text-emerald-700" data-testid="anzahlung-rest">{eur2(rest)}</span></div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} data-testid="anzahlung-cancel" className="flex-1 border border-zinc-300 text-zinc-700 text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-zinc-50">Abbrechen</button>
          <button onClick={() => onConfirm(val)} disabled={loading || val <= 0} data-testid="anzahlung-confirm"
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <ReceiptText size={14}/>} Erstellen &amp; PDF
          </button>
        </div>
      </div>
    </div>
  );
}
